import React, { useState } from 'react';
import { PROBLEM, LANGS, ASSIST_ACTIONS } from './dsaProblem';
import { IconChevDown, IconChevRight, IconSpark, IconCols, IconRows } from './icons';

/*
 * DsaCard — an inline coding workspace rendered INSIDE the chat thread
 * (problem · editor · testcases · assistant actions). It is controlled:
 * code/lang live in NanoChat so the main composer shares the same context.
 * Run / Submit / assistant chips call onAsk(prompt, label), which posts the
 * question + AI reply into the same continuous conversation.
 *
 * `split` toggles the problem/editor arrangement: 'col' (stacked) or
 * 'row' (side-by-side).
 */
const DsaCard = ({ code, onCodeChange, lang, onLangChange, onAsk, disabled, active = true }) => {
  const [activeCase, setActiveCase] = useState(0);
  const [problemOpen, setProblemOpen] = useState(true);
  const [split, setSplit] = useState('col');

  const lineCount = code.split('\n').length;
  const toggleProblem = () => setProblemOpen((o) => !o);

  const run = () => {
    const tc = PROBLEM.testcases[activeCase];
    onAsk(
      `Trace my code on this test case: ${tc.input} (expected output: ${tc.expected}). Show the step-by-step result, state the actual output, and whether it matches. Keep it brief.`,
      `Run · ${tc.name}`
    );
  };
  const submit = () => onAsk(
    'Evaluate my solution against all constraints and typical edge cases (empty string, single character, all-same chars, all-unique). Is it correct and optimal? Give a short verdict.',
    'Submit solution'
  );

  return (
    <div className={`nano-dsacard${active ? '' : ' inactive'}`}>
      {/* Header: problem title + collapse + split toggle */}
      <div className="nano-dsacard-head">
        <span className="nano-dsacard-caret" onClick={toggleProblem}>
          {problemOpen ? <IconChevDown size={15} /> : <IconChevRight size={15} />}
        </span>
        <div style={{ flex: 1, minWidth: 0 }} onClick={toggleProblem}>
          <div className="nano-dsacard-title">
            {PROBLEM.num}. {PROBLEM.title}
            {!active && <span className="nano-dsacard-ro">Read-only</span>}
          </div>
          <div className="nano-dsa-tags">
            <span className="nano-dsa-tag diff">{PROBLEM.difficulty}</span>
            {PROBLEM.tags.map((t) => <span key={t} className="nano-dsa-tag">{t}</span>)}
          </div>
        </div>
        <div className="nano-dsacard-split-toggle">
          <button className={split === 'col' ? 'on' : ''} onClick={() => setSplit('col')} title="Stacked (vertical split)" type="button"><IconRows size={15} /></button>
          <button className={split === 'row' ? 'on' : ''} onClick={() => setSplit('row')} title="Side by side (horizontal split)" type="button"><IconCols size={15} /></button>
        </div>
      </div>

      <div className={`nano-dsacard-split ${split === 'row' ? 'is-row' : 'is-col'}`}>
        {problemOpen && (
          <div className="nano-dsacard-problem">
            <p className="nano-dsa-stmt">{PROBLEM.statement}</p>
            {PROBLEM.examples.map((ex, i) => (
              <div key={i} className="nano-dsa-example">
                <div className="nano-dsa-ex-h">Example {i + 1}:</div>
                <div className="nano-dsa-ex-box">
                  <div><b>Input:</b> {ex.input}</div>
                  <div><b>Output:</b> {ex.output}</div>
                  {ex.explanation && <div><b>Explanation:</b> {ex.explanation}</div>}
                </div>
              </div>
            ))}
            <div className="nano-dsa-ex-h">Constraints:</div>
            <ul className="nano-dsa-constraints">
              {PROBLEM.constraints.map((c) => <li key={c}><code>{c}</code></li>)}
            </ul>
          </div>
        )}

        <div className="nano-dsacard-main">
          {/* Editor */}
          <div className="nano-dsacard-editor">
            <div className="nano-dsa-editor-head">
              <span className="nano-dsa-panel-lbl">Code Editor</span>
              <select className="nano-dsa-lang" value={lang} disabled={!active} onChange={(e) => onLangChange?.(e.target.value)}>
                {LANGS.map((l) => <option key={l}>{l}</option>)}
              </select>
            </div>
            <div className="nano-dsa-code">
              <div className="nano-dsa-gutter">
                {Array.from({ length: lineCount }).map((_, i) => <div key={i}>{i + 1}</div>)}
              </div>
              <textarea
                className="nano-dsa-textarea"
                value={code}
                spellCheck={false}
                readOnly={!active}
                onChange={(e) => onCodeChange?.(e.target.value)}
                rows={lineCount}
              />
            </div>
            <div className="nano-dsa-editor-actions">
              <button className="nano-dsa-run" onClick={run} disabled={disabled} type="button">Run</button>
              <button className="nano-dsa-submit" onClick={submit} disabled={disabled} type="button">Submit</button>
            </div>
          </div>

          {/* Test cases */}
          <div className="nano-dsacard-tests">
            <div className="nano-dsa-case-tabs">
              {PROBLEM.testcases.map((tc, i) => (
                <button key={tc.name} className={`nano-dsa-case${activeCase === i ? ' active' : ''}`} onClick={() => setActiveCase(i)} type="button">
                  {tc.name}
                </button>
              ))}
            </div>
            <div className="nano-dsa-case-io">
              <span className="lbl">Input</span>
              <span className="box">{PROBLEM.testcases[activeCase].input}</span>
              <span className="lbl">Expected</span>
              <span className="box">{PROBLEM.testcases[activeCase].expected}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Assistant actions — responses post into the chat thread */}
      <div className="nano-dsacard-chips">
        <span className="nano-dsa-assist-lbl"><IconSpark size={14} /> AI Assistant</span>
        {ASSIST_ACTIONS.map((a) => (
          <button key={a.key} className="nano-dsa-chip" onClick={() => onAsk(a.prompt, a.label)} disabled={disabled} type="button">
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default DsaCard;
